import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {
  filtersBehavioralIndicatorsEntitiesCollectionSelector,
  filtersCharacterTraitsEntitiesCollectionSelector,
  filtersEthnicitiesEntitiesCollectionSelector,
  filtersGendersEntitiesCollectionSelector,
  filtersImageSourcesEntitiesCollectionSelector,
  filtersPublishedOutputToolsEntitiesCollectionSelector,
  resourcesConcatenatedBaseEntitiesCollectionSelector,
  resourcesEntitiesCollectionSelector,
} from 'filters-selections';
import {accessibilityStatusOptions} from 'admin/resources/upload/enums';
import {buildLookupNameIndex, IResourceMetadataLookupNames} from './mapResourceLookups';

/** Builds id→name indexes for the metadata selects that must submit both values. */
export const useResourceMetadataLookupNames = (): IResourceMetadataLookupNames => {
  const imageSources = useSelector(filtersImageSourcesEntitiesCollectionSelector);
  const ethnicities = useSelector(filtersEthnicitiesEntitiesCollectionSelector);
  const genders = useSelector(filtersGendersEntitiesCollectionSelector);
  const publishedOutputTools = useSelector(filtersPublishedOutputToolsEntitiesCollectionSelector);
  const associatedResources = useSelector(resourcesEntitiesCollectionSelector);
  // Survives search refreshes that replace `resources` (keeps previously seen titles).
  const associatedResourcesHistory = useSelector(resourcesConcatenatedBaseEntitiesCollectionSelector);
  const characterTraits = useSelector(filtersCharacterTraitsEntitiesCollectionSelector);
  const behavioralIndicatorGroups = useSelector(filtersBehavioralIndicatorsEntitiesCollectionSelector);

  return useMemo(() => {
    const behavioralIndicators = (behavioralIndicatorGroups ?? []).flatMap((group) => group.indicators ?? []);

    return {
      imageSources: buildLookupNameIndex(imageSources),
      ethnicities: buildLookupNameIndex(ethnicities),
      genders: buildLookupNameIndex(genders),
      publishedOutputTools: buildLookupNameIndex(publishedOutputTools),
      associatedResources: buildLookupNameIndex([
        ...(associatedResourcesHistory ?? []),
        ...(associatedResources ?? []),
      ]),
      characterTraits: buildLookupNameIndex(characterTraits),
      behavioralIndicators: buildLookupNameIndex(behavioralIndicators),
      accessibilityStatuses: buildLookupNameIndex(
        accessibilityStatusOptions.map((option) => ({id: option.value, name: option.label}))
      ),
    };
  }, [
    imageSources,
    ethnicities,
    genders,
    publishedOutputTools,
    associatedResources,
    associatedResourcesHistory,
    characterTraits,
    behavioralIndicatorGroups,
  ]);
};
